import { ReactElement, useContext, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Tooltip, useTheme
} from "@mui/material";
import { SettingsApplications } from "@mui/icons-material";
import { DashboardSchemaType, IAcquisitionCell, ICell, IDashboard, IDashboardResult, IToolbox } from "../types";
import DialogFilter from "./dialogFilter";
import { IDictionary, IParentSize, ISelectedNode, IState, Modules } from "../../../types";
import MainWrapper from "./wrappers/mainWrapper";
import DialogAcqConfigCell from "./dialogAcqConfigCell";
import AcquisitionChart from "./wrappers/acquisitionChart";
import { SystemContext } from "../../../context/systemContext";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import VerticalAlignCenterIcon from "@mui/icons-material/VerticalAlignCenter";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SearchIcon from "@mui/icons-material/Search";
import { cloneObject, onCapture, saveBlobAs, saveZipAs } from "../../../system/utils";
import GetAppIcon from "@mui/icons-material/GetApp";
import { generateAcquisitionCsv, generateCsv, getSchema } from "./utils";
import { useSelector } from "react-redux";
import { useLazyFetch } from "../../../system/useFetch";
import { DateTime } from "luxon";
import IndicatorPanel from "./wrappers/indicatorPanel";
import SpeedIcon from "@mui/icons-material/Speed";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ListIcon from "@mui/icons-material/List";

interface IProps {
  cell: ICell | IAcquisitionCell;
  responseData:  IDashboardResult | null;
  loading: boolean;
  parentSize: IParentSize;
  dashboard: IDashboard;
  setDashboard: (dashboard: IDashboard) => void;
  setSelectedCell: (dashboard: ICell | IAcquisitionCell | null) => void;
  isMaximized: boolean;
  schemas: Record<DashboardSchemaType, any>;
  onUpdatePalette: (p: IDictionary<string>, hasChange: boolean) => void;
  disableSettings?: boolean;
  marqueeMode?: boolean;
}

const CellComponent = ({ cell, responseData, loading, parentSize, dashboard, setDashboard, setSelectedCell, isMaximized, schemas, onUpdatePalette, disableSettings, marqueeMode }: IProps): ReactElement => {
  const theme = useTheme();
  const systemContext  = useContext(SystemContext);
  const selectedNode = useSelector<IState, ISelectedNode>(state => state.selectedNode);
  const node = useSelector<IState, ISelectedNode>(state => state.selectedNode);
  const [ open, setOpen ] = useState(false);
  const [ maximized, setMaximized ] = useState(isMaximized);
  const [ waiting, setWaiting ] = useState<boolean>(false);
  const [ adjustAxes, setAdjustAxes ] = useState<boolean>(false);
  const [ indicatorMode, setIndicatorMode ] = useState<boolean>(false);
  const [ showAccumulated, setShowAccumulated ] = useState<boolean>(false);
  const [ toolbox, setToolbox ] = useState<IToolbox>({ cursor: false, zoom: false, legend: true });
  const [ getDashboardData, { data: responseZoomData }, clearData ] = useLazyFetch<IDashboardResult>(true);

  useEffect(() => {
    setToolbox({ cursor: false, zoom: false, legend: true });
    setShowAccumulated(false);
  }, [ responseData ]);

  useEffect(() => {

    setIndicatorMode((cell as IAcquisitionCell).indicatorMode ?? false);

  }, [cell, open]);

  const processCapture = () => {
    setWaiting(true);

    setTimeout(() => {
      onCapture(cell.key, cell.title)
        .then(() => {
          setWaiting(false);
        });
    }, 500);
  };

  const onMaximized = (state: boolean) => {
    setMaximized(state);
    setSelectedCell(state ? cell : null);
  };

  const exportRawData = () => {
    if (responseData && responseData[cell.key]) {
      if (dashboard.isAcquisition) {
        const files = generateAcquisitionCsv(cell as IAcquisitionCell, responseData[cell.key].result);

        if (files) {
          saveZipAs(files, `${dashboard.name}-${cell.title}.zip`).then();
        }
      } else if("dimension" in cell) {
        const file = generateCsv(cell, responseData[cell.key].result, getSchema(schemas, cell.module));
        saveBlobAs(URL.createObjectURL(file), `${dashboard.name}-${cell.title}.csv`);
      }
    }
  };

  const onLegend = () => {
    setToolbox({ ...toolbox, legend: !toolbox.legend });
  };

  const onCursorMode = () => {
    setToolbox({ ...toolbox, cursor: !toolbox.cursor });
  };

  const onZoomMode = () => {
    if(toolbox.zoom){
      clearData();
    }

    setToolbox({ ...toolbox, zoom: !toolbox.zoom });
  };

  const onZoomArea = (from: number, to: number): void => {
    getDashboardData({
      module: "dashboard",
      query: "getDashboardData",
      payload: {
        nodeID: selectedNode.nodeID,
        nodeType: selectedNode.type,
        moduleID: Modules.INTERRUPTIONS,
        from: DateTime.fromSeconds(from).toFormat("yyyy-MM-dd HH:mm:ss"),
        to: DateTime.fromSeconds(to).toFormat("yyyy-MM-dd HH:mm:ss"),
        cells: [ cell ]
      }
    });
  };

  const onIndicatorMode = (): void => {
    const value = !(cell as IAcquisitionCell).indicatorMode;

    (cell as IAcquisitionCell).indicatorMode = value;

    setDashboard(cloneObject(dashboard));

    setIndicatorMode(value);
  };

  const editAllowed = dashboard.userID === 0 || systemContext.user?.userID === dashboard.userID || dashboard.isEditable;

  return (
    <Card style={{ position: "relative" }}>
      <CardHeader sx={{
        paddingTop: "0.5em",
        paddingRight: "0.5em",
        paddingLeft: "0.5em",
        paddingBottom: 0,
      }}
      title={<div className="display-flex-row">
        {loading && <CircularProgress className="mr-1" size={22} />}
        {cell.title}
      </div>}
      action={
        <>
          {"chart" in cell &&  (cell.chart === "BAR" || cell.chart === "STACKED_BAR") &&
              <IconButton sx={{
                marginRight: "6px",
                marginTop: "6px",
                padding: "6px"
              }} color={showAccumulated ? "secondary" : "primary"} onClick={() => setShowAccumulated(!showAccumulated)}>
                <Tooltip title={"Accumulated"}>
                  <TrendingUpIcon/>
                </Tooltip>
              </IconButton>
          }

          {responseData && responseData[cell.key] && dashboard.isAcquisition &&
              <IconButton sx={{
                marginRight: "6px",
                marginTop: "6px",
                padding: "6px"
              }} color={indicatorMode ? "secondary" : "primary"} onClick={onIndicatorMode}>
                <Tooltip title={"Indicator Mode"}>
                  <SpeedIcon />
                </Tooltip>
              </IconButton>
          }


          {responseData && responseData[cell.key] && dashboard.isAcquisition && !indicatorMode &&
              <>
                <IconButton sx={{
                  marginRight: "6px",
                  marginTop: "6px",
                  padding: "6px"
                }} color={toolbox.legend ? "secondary" : "primary"} onClick={onLegend}>
                  <Tooltip title={"Legend"}>
                    <ListIcon/>
                  </Tooltip>
                </IconButton>
                <IconButton sx={{
                  marginRight: "6px",
                  marginTop: "6px",
                  padding: "6px"
                }} color={toolbox.cursor ? "secondary" : "primary"} onClick={onCursorMode}>
                  <Tooltip title={"Cursor"}>
                    <VerticalAlignCenterIcon transform={"rotate(90)"}/>
                  </Tooltip>
                </IconButton>
                <IconButton sx={{
                  marginRight: "6px",
                  marginTop: "6px",
                  padding: "6px"
                }} color={toolbox.zoom ? "secondary" : "primary"} onClick={onZoomMode}
                disabled={(cell as IAcquisitionCell).stackedAxes ?? false}>
                  <Tooltip title={"Cursor"}>
                    <SearchIcon/>
                  </Tooltip>
                </IconButton>

                <IconButton sx={{
                  marginRight: "6px",
                  marginTop: "6px",
                  padding: "6px"
                }} color={toolbox.zoom ? "secondary" : "primary"} onClick={() => setAdjustAxes(!adjustAxes)}
                disabled={(cell as IAcquisitionCell).stackedAxes ?? false}>
                  <Tooltip title={"Adjust Axes"}>
                    {adjustAxes ?  <UnfoldLessIcon/> : <UnfoldMoreIcon />}
                  </Tooltip>
                </IconButton>
              </>
          }
          {responseData && responseData[cell.key] &&
              <IconButton sx={{
                marginRight: "6px",
                marginTop: "6px",
                padding: "6px"
              }} color="primary" onClick={exportRawData}>
                <Tooltip title={"Export raw data"}>
                  <GetAppIcon/>
                </Tooltip>
              </IconButton>
          }
          {maximized
            ? <IconButton sx={{
              marginRight: "6px",
              marginTop: "6px",
              padding: "6px"
            }} color="primary"
            disabled={marqueeMode}
            onClick={() => onMaximized(false)} >
              <Tooltip title={"Minimize"}>
                <FullscreenExitIcon />
              </Tooltip>
            </IconButton>
            : <IconButton sx={{
              marginRight: "6px",
              marginTop: "6px",
              padding: "6px"
            }} color="primary"
            disabled={marqueeMode}
            onClick={() => onMaximized(true)} >
              <Tooltip title={"Maximize"}>
                <FullscreenIcon/>
              </Tooltip>
            </IconButton>
          }
          {waiting
            ? <CircularProgress className=""/>
            : <IconButton sx={{
              marginRight: "6px",
              marginTop: "6px",
              padding: "6px"
            }}  color="primary" onClick={processCapture}>
              <Tooltip title={"Snapshot"}>
                <CameraAltIcon/>
              </Tooltip>
            </IconButton>
          }

          {!disableSettings &&
            <IconButton sx={{
              marginRight: "6px",
              marginTop: "6px",
              padding: "6px"
            }} color="primary" onClick={() => setOpen(true)} disabled={!editAllowed}>
              <Tooltip title={"Config"}>
                <SettingsApplications/>
              </Tooltip>
            </IconButton>
          }
        </>
      }/>
      <CardContent sx={{ padding: "0px !important" }}>
        <div id={cell.key} style={{ marginTop: "2px", background: theme.palette.background.paper }}>
          { loading && !responseData &&
            <div className="display-flex-row justify-content-center align-items-center" style={{ height: parentSize.height - 64 }}>
              <CircularProgress />
            </div>
          }
          { responseData && responseData[cell.key] && Array.isArray(responseData[cell.key].result) && responseData[cell.key].result.length > 0 && !dashboard.isAcquisition &&
          <MainWrapper  cell={cell as ICell} data={responseData[cell.key]} showAccumulated={showAccumulated}
            parentSize={{ width: parentSize.width, height: parentSize.height - 60 }}  schemas={schemas}
            palette={dashboard.layout.palette} updatePalette={onUpdatePalette} node={node}/>
          }
          { responseData && responseData[cell.key] && dashboard.isAcquisition && !indicatorMode &&
            <AcquisitionChart cell={cell as IAcquisitionCell} schemas={schemas}
              data={toolbox.zoom && responseZoomData ? responseZoomData[cell.key].result : responseData[cell.key].result}
              parentSize={{ width: parentSize.width, height: parentSize.height - 60 }}
              palette={dashboard.layout.palette} updatePalette={onUpdatePalette}
              toolbox={toolbox} onZoomArea={onZoomArea} adjustAxes={adjustAxes}/>
          }

          { responseData && responseData[cell.key] && dashboard.isAcquisition && indicatorMode &&
            <IndicatorPanel cell={cell as IAcquisitionCell} schemas={schemas}
              data={toolbox.zoom && responseZoomData ? responseZoomData[cell.key].result : responseData[cell.key].result}
              parentSize={{ width: parentSize.width, height: parentSize.height - 60 }}
              palette={dashboard.layout.palette} updatePalette={onUpdatePalette}
              toolbox={toolbox} onZoomArea={onZoomArea}/>
          }
        </div>
        {loading && responseData && responseData[cell.key] && <div style={{
          background: "rgba(0, 0, 0, 0.5)",
          position: "absolute",
          width: "100%",
          height: "100%",
          top: 40,
          left: 0
        }}/>}
      </CardContent>

      {!dashboard.isAcquisition && <DialogFilter open={open} setOpen={setOpen} cell={cell as ICell} schemas={schemas}/>}

      {dashboard.isAcquisition && <DialogAcqConfigCell open={open} setOpen={setOpen} cell={cell as IAcquisitionCell} schema={schemas[DashboardSchemaType.Acquisition]}/>}
    </Card>
  );
};

export default CellComponent;
